import { Pencil } from "lucide-react";
import {
  useGetRecordRepresentation,
  RecordRepresentation,
  useGetIdentity,
  useGetOne,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Button } from "@/components/ui/button";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { Markdown } from "../misc/Markdown";
import { MobileBackButton } from "../misc/MobileBackButton";
import { formatDate } from "../misc/formatDate";
import type { ContactNote } from "../types";
import { NoteEditSheet } from "./NoteEditSheet";
import { useGetSalesName } from "../sales/useGetSalesName";

export const NoteShowPage = () => {
  const translate = useTranslate();
  const { id: contactId, noteId } = useParams<{
    id: string;
    noteId: string;
  }>();
  const [editOpen, setEditOpen] = useState(false);
  const getContactRepresentation = useGetRecordRepresentation("contacts");

  const { data: note, isPending } = useGetOne<ContactNote>("contact_notes", {
    id: noteId!,
  });
  const { identity } = useGetIdentity();
  const isCurrentUser = note?.sales_id === identity?.id;
  const salesName = useGetSalesName(note?.sales_id, {
    enabled: note && !isCurrentUser,
  });

  if (isPending || !note) return null;

  return (
    <>
      <NoteEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        noteId={note.id}
      />
      <MobileHeader>
        <MobileBackButton to={`/contacts/${contactId}/show`} />
        <div className="flex flex-1 min-w-0">
          <Link to={`/contacts/${contactId}/show`} className="flex-1 min-w-0">
            <h1 className="truncate text-xl font-semibold text-brand-navy">
              <ReferenceField
                record={note}
                resource="contact_notes"
                source="contact_id"
                reference="contacts"
                link={false}
                render={({ referenceRecord }) =>
                  referenceRecord
                    ? translate("resources.notes.note_for_contact", {
                        name: getContactRepresentation(referenceRecord),
                      })
                    : null
                }
              >
                <RecordRepresentation resource="contacts" />
              </ReferenceField>
            </h1>
          </Link>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="size-5" />
          <span className="sr-only">
            {translate("resources.notes.action.edit")}
          </span>
        </Button>
      </MobileHeader>
      <MobileContent>
        <div className="mb-4">
          <div className="flex items-center space-x-2 w-full text-sm text-muted-foreground">
            <span>
              {isCurrentUser ? translate("resources.notes.me") : salesName}{" "}
            </span>
            <div className="flex-1" />
            {formatDate(note.date)}
          </div>
        </div>

        {note.text && <Markdown className="text-sm">{note.text}</Markdown>}
      </MobileContent>
    </>
  );
};
